import { useEffect, useState } from 'react';

/**
 * Minimale router op de History API. Routes:
 *   /                     Home
 *   /library/:key         Bibliotheek bladeren
 *   /item/:ratingKey      Detailpagina (film, serie, seizoen, aflevering)
 *   /play/:ratingKey      Player
 *   /search               Zoeken
 *   /live, /live/guide    Live TV: sport en gids
 *   /live/play/:acc/:id   Live-player (?start=&end= voor terugkijken)
 *   /live/multi           Multiview (2x2)
 *   /settings             Instellingen (IPTV, Seerr, thema)
 *   /requests             Verzoeken via Seerr
 *   /hub/:id              Een hub (Home is de eerste)
 *   /settings/home        Startscherm-editor
 *   /settings/skin(/:id)  Skin-studio (nieuw of een bestaande skin bewerken)
 *   /stats                Jouw jaar (kijkoverzicht)
 *   /browse/:kind/:id     Netwerk, studio of categorie (?media=movie|tv)
 *   /settings/status      Statuspagina (eigenaar)
 *   /settings/profiles    Profielbeheer (eigenaar)
 *   /together             Samen kijken: code invoeren (?code=)
 *   /remote               Telefoon als afstandsbediening (?code=)
 *   /random               "Wat zullen we kijken?"
 *   /calendar             Kalender: nieuwe afleveringen en films
 *   /collections(/:id)    Collecties: filmreeksen en lijsten; een reeks openen
 *   /mylist               Mijn lijst (Plex-watchlist)
 *   /sport/:id            Competitiepagina (sportwidget met logo's)
 *   /addon/:id/:type/:metaId  Titel uit een addon-catalogus
 *   /addon-play?key=     Addon-bron afspelen
 *   /music(/:section)    Muziek (?q= zoeken, ?id= voor album/artiest/playlist)
 *   /sportcenter(/:tab)  Sport: wedstrijden, clubs, standen (?league=), match (?path=&event=), club (?path=&id=)
 *   /person/:id           Acteur/regisseur (TMDB-id); /person?name= zoekt op naam
 */

export type Route =
  | { name: 'home' }
  | { name: 'library'; key: string }
  | { name: 'item'; ratingKey: string }
  | { name: 'play'; ratingKey: string; params: URLSearchParams }
  | { name: 'search'; query: string }
  | { name: 'live'; tab: 'sport' | 'guide' }
  | { name: 'livePlay'; accountId: string; streamId: number; start?: number; end?: number }
  | { name: 'multiview' }
  | { name: 'settings'; section?: string }
  | { name: 'requests'; query: string }
  | { name: 'hub'; id: string }
  | { name: 'editor' }
  | { name: 'calendar' }
  | { name: 'mylist' }
  | { name: 'collections' }
  | { name: 'listView'; src: string }
  | { name: 'saga'; id: number }
  | { name: 'sportLeague'; id: string }
  | { name: 'random' }
  | { name: 'stats' }
  | { name: 'browse'; kind: 'company' | 'network' | 'keyword'; id: number; media: 'movie' | 'tv' | null }
  | { name: 'status' }
  | { name: 'profilesAdmin' }
  | { name: 'together'; code: string }
  | { name: 'remote'; code: string }
  | { name: 'studio'; id: string | null }
  | { name: 'person'; id: number | null; lookup: string }
  | { name: 'addonTitle'; addonId: string; type: string; id: string }
  | { name: 'addonPlay'; params: URLSearchParams }
  | { name: 'music'; section: string; params: URLSearchParams }
  | { name: 'server'; tab: string }
  | { name: 'sportcenter'; tab: string; params: URLSearchParams };

function parse(): Route {
  const { pathname, search } = window.location;
  const params = new URLSearchParams(search);
  const parts = pathname.split('/').filter(Boolean).map(decodeURIComponent);
  if (parts[0] === 'library' && parts[1]) return { name: 'library', key: parts[1] };
  if (parts[0] === 'item' && parts[1]) return { name: 'item', ratingKey: parts[1] };
  if (parts[0] === 'play' && parts[1]) return { name: 'play', ratingKey: parts[1], params };
  if (parts[0] === 'search') return { name: 'search', query: params.get('q') ?? '' };
  if (parts[0] === 'live' && parts[1] === 'play' && parts[2] && parts[3]) {
    return { name: 'livePlay', accountId: parts[2], streamId: Number(parts[3]), start: Number(params.get('start')) || undefined, end: Number(params.get('end')) || undefined };
  }
  if (parts[0] === 'live' && parts[1] === 'multi') return { name: 'multiview' };
  if (parts[0] === 'live') return { name: 'live', tab: parts[1] === 'guide' ? 'guide' : 'sport' };
  if (parts[0] === 'settings' && parts[1] === 'home') return { name: 'editor' };
  if (parts[0] === 'settings' && parts[1] === 'status') return { name: 'status' };
  if (parts[0] === 'settings' && parts[1] === 'profiles') return { name: 'profilesAdmin' };
  if (parts[0] === 'together') return { name: 'together', code: (params.get('code') ?? '').toUpperCase() };
  if (parts[0] === 'remote') return { name: 'remote', code: params.get('code') ?? '' };
  if (parts[0] === 'settings' && parts[1] === 'skin') return { name: 'studio', id: parts[2] ?? null };
  if (parts[0] === 'settings') return { name: 'settings', section: parts[1] };
  if (parts[0] === 'hub' && parts[1]) return { name: 'hub', id: parts[1] };
  if (parts[0] === 'calendar') return { name: 'calendar' };
  if (parts[0] === 'mylist') return { name: 'mylist' };
  if (parts[0] === 'collections' && parts[1] === 'list') return { name: 'listView', src: params.get('src') ?? '' };
  if (parts[0] === 'collections' && parts[1]) return { name: 'saga', id: Number(parts[1]) || 0 };
  if (parts[0] === 'collections') return { name: 'collections' };
  if (parts[0] === 'sport' && parts[1]) return { name: 'sportLeague', id: parts[1] };
  if (parts[0] === 'random') return { name: 'random' };
  if (parts[0] === 'stats') return { name: 'stats' };
  if (parts[0] === 'browse' && ['company', 'network', 'keyword'].includes(parts[1]) && Number(parts[2])) {
    const m = params.get('media');
    return { name: 'browse', kind: parts[1] as 'company', id: Number(parts[2]), media: m === 'movie' || m === 'tv' ? m : null };
  }
  if (parts[0] === 'addon' && parts[1] && parts[2] && parts[3]) return { name: 'addonTitle', addonId: parts[1], type: parts[2], id: parts.slice(3).join('/') };
  if (parts[0] === 'addon-play') return { name: 'addonPlay', params };
  if (parts[0] === 'sportcenter') return { name: 'sportcenter', tab: parts[1] ?? 'today', params };
  if (parts[0] === 'server') return { name: 'server', tab: parts[1] ?? 'active' };
  if (parts[0] === 'music') return { name: 'music', section: parts[1] ?? 'home', params };
  if (parts[0] === 'person') return { name: 'person', id: parts[1] ? Number(parts[1]) || null : null, lookup: params.get('name') ?? '' };
  if (parts[0] === 'requests') return { name: 'requests', query: params.get('q') ?? '' };
  return { name: 'home' };
}

const listeners = new Set<() => void>();
window.addEventListener('popstate', () => listeners.forEach((l) => l()));

export function navigate(to: string, opts: { replace?: boolean } = {}): void {
  if (opts.replace) history.replaceState(null, '', to);
  else history.pushState(null, '', to);
  window.scrollTo(0, 0);
  listeners.forEach((l) => l());
}

export function goBack(): void {
  if (history.length > 1 && parse().name !== 'home') history.back();
  else navigate('/', { replace: true });
}

export function useRoute(): Route {
  const [route, setRoute] = useState(parse);
  useEffect(() => {
    const l = () => setRoute(parse());
    listeners.add(l);
    return () => {
      listeners.delete(l);
    };
  }, []);
  return route;
}

/** Klikbare link die de router gebruikt (gewone <a> voor toegankelijkheid en "openen in nieuw tabblad"). */
export function linkProps(to: string) {
  return {
    href: to,
    onClick: (e: { preventDefault: () => void; metaKey?: boolean; ctrlKey?: boolean }) => {
      if (e.metaKey || e.ctrlKey) return;
      e.preventDefault();
      navigate(to);
    },
  };
}
